import { Autocomplete, Box, LinearProgress, TextField } from "@mui/material";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import Skeletons from "../../../../components/loader/Skeletons";
import useUser from "../../../../hooks/useUser";
import { useFetch } from "../../../../hooks/Fetching";
import { CLINICS } from "../../../../enums/QueryKeys";

export default function ChangeClinic({ registrationKey = "clinic" }) {
  const {
    isLoading: isUserLoading,
    profile,
    currentClinic,
    error: userError,
    roles,
  } = useUser();
  const methods = useFormContext();
  const { t } = useTranslation();
  const {
    data: clinics,
    isLoading: isClinicsLoading,
    error: clinicsError,
  } = useFetch("/clinics/list", null, CLINICS);

  React.useEffect(() => {
    const userclinic = currentClinic?.id ? currentClinic : null;

    methods.setValue("clinic", userclinic);
  }, [profile, currentClinic]);

  if (isUserLoading || isClinicsLoading) {
    return <Skeletons grid={false} />;
  } else if (userError || clinicsError) {
    return (
      <SimpleAlert severity={"error"} message={"ErrorFetchingClinicList"} />
    );
  }

  return (
    <Controller
      name={registrationKey}
      control={methods.control}
      {...methods.register(registrationKey)}
      render={({ field, fieldState }) => (
        <Autocomplete
          fullWidth
          {...field}
          value={field.value ?? null}
          defaultValue={clinics?.find(
            (clinic) => Number(clinic?.id) == Number(currentClinic?.id),
          )}
          onChange={(e, value) => {
            field.onChange(value);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          options={clinics}
          getOptionLabel={(option) =>
            `${option.title} - ${option.streetAddress}, ${option?.city}`
          }
          renderOption={(props, option) => (
            <Box {...props} key={option.id}>
              {option.title} - {option.streetAddress}, {option.city}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Clinic")}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputProps={{
                ...params.inputProps,
                "data-testid": "profile.settings.clinic-input",
              }}
            />
          )}
          clearText={t("Clear")}
          closeText={t("Close")}
          openText={t("Open")}
        />
      )}
    />
  );
}

export function ChangeClinicMaster({ currentClinic }) {
  const {
    data: clinics,
    isLoading,
    error,
  } = useFetch("/clinics/list", null, CLINICS);

  const methods = useFormContext();
  const { t } = useTranslation();

  React.useEffect(() => {
    if (currentClinic == null && !isLoading) {
      methods.setError("clinic", {
        type: "custom",
        message: t("PleaseChooseAClinic"),
      });
    }
  }, []);

  React.useEffect(() => {
    methods.setValue("clinic", currentClinic);
  }, [currentClinic]);

  if (isLoading) {
    return <LinearProgress />;
  }
  if (error) {
    return (
      <SimpleAlert severity={"error"} message={"ErrorFetchingListOfClinics"} />
    );
  }

  return (
    <Controller
      name={"clinic"}
      control={methods.control}
      {...methods.register("clinic")}
      render={({ field, fieldState }) => (
        <Autocomplete
          fullWidth
          {...field}
          value={field.value ?? null}
          defaultValue={clinics?.find(
            (clinic) => Number(clinic?.id) == Number(currentClinic?.id),
          )}
          onChange={(e, value) => {
            field.onChange(value);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          options={clinics}
          getOptionLabel={(option) =>
            `${option.title} - ${option.streetAddress}, ${option?.city}`
          }
          renderOption={(props, option) => (
            <Box {...props} key={option.id}>
              {option.title} - {option.streetAddress}, {option.city}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Clinic")}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputProps={{
                ...params.inputProps,
                "data-testid": "profile.settings.clinic-input",
              }}
            />
          )}
          clearText={t("Clear")}
          closeText={t("Close")}
          openText={t("Open")}
        />
      )}
    />
  );
}

export function ClinicPickerMaster({ registrationKey = "clinic" }) {
  const fetch = useFetch("/clinics/");
  const methods = useFormContext();
  const { t } = useTranslation();

  if (fetch.isLoading) {
    return <Skeletons grid={false} />;
  } else if (fetch.error) {
    return (
      <SimpleAlert severity={"error"} message={"ErrorFetchingClinicList"} />
    );
  }

  const clinics = fetch.data;

  return (
    <Controller
      name={registrationKey}
      control={methods.control}
      {...methods.register(registrationKey)}
      render={({ field, fieldState }) => (
        <Autocomplete
          fullWidth
          {...field}
          value={field.value ?? null}
          onChange={(e, value) => {
            field.onChange(value);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          options={clinics}
          getOptionLabel={(option) =>
            `${option.title} - ${option.streetAddress}, ${option?.city}`
          }
          renderOption={(props, option) => (
            <Box {...props} key={option.id}>
              {option.title} - {option.streetAddress}, {option.city}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Clinic")}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputProps={{
                ...params.inputProps,
                "data-testid": "users.new-user-form.clinic-input",
              }}
            />
          )}
          clearText={t("Clear")}
          closeText={t("Close")}
          openText={t("Open")}
        />
      )}
    />
  );
}
